import React, { Fragment, useEffect } from "react";
import { Switch, Route, useHistory, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import NotFound from "../screens/NotFound";
import routeMap from "./routes";

import useUserAgent from "../hooks/useUserAgent";

import { checkAppVersion } from "@flights/web-api-utils-universal";
import LoadingStatus from "../components/elements/LoadingStatus";
import { I18nChildContext, t, useI18n } from "@bookingcom/lingojs-react";
import useGlobalContext from "../hooks/useGlobalContext";
import "./global.css";
import BoundsRedirect from "./BoundsRedirect";

import { A11Y_MOBILE_QUERY_PARAMS } from "@flights/constants";

type RendererProps = {
  component: JSX.Element;
};

const Renderer = ({ component }: RendererProps) => {
  if (checkAppVersion.isAppVersionMismatch()) {
    window.location.reload();
  }
  return component;
};

const Layout = () => {
  const { isMobile, isWebview } = useUserAgent();
  const { isPaymentCollectionPage, isAlternativeSeatmap = false } = useGlobalContext();
  const routes = routeMap({
    isMobile,
    isPaymentCollectionPage,
    isWebview,
    isAlternativeSeatmap
  }).filter((route) => !route.exclude);
  const i18n = useI18n() as I18nChildContext;
  const NavigationMiddleware = () => {
    const history = useHistory();
    const location = useLocation();

    useEffect(() => {
      const preserveA11yMobileView = () => {
        const currentParams = new URLSearchParams(location.search);
        const a11yMobileView = currentParams.get(A11Y_MOBILE_QUERY_PARAMS);

        if (a11yMobileView === "1") {
          // If we're navigating to a new route, preserve the parameter
          const unlisten = history.listen((newLocation) => {
            const newParams = new URLSearchParams(newLocation.search);
            if (!newParams.has(A11Y_MOBILE_QUERY_PARAMS)) {
              newParams.set(A11Y_MOBILE_QUERY_PARAMS, "1");
              history.replace({
                ...newLocation,
                search: newParams.toString()
              });
            }
          });
          return unlisten;
        }
        return () => {};
      };

      const unlisten = preserveA11yMobileView();
      return () => unlisten();
    }, [history, location]);

    return null;
  };
  return (
    <Fragment>
      <Helmet>
        <title>{i18n.trans(t("flights_page_title_bp_generic"))}</title>
        <meta property="webview:header" content="regular" />
        <meta property="webview:title" content={""} />
        {isWebview && (
          <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1" />
        )}
      </Helmet>
      <NavigationMiddleware />

      <Switch>
        {routes.map((route) => {
          // eslint-disable-next-line @typescript-eslint/naming-convention -- this line was auto generated, hence fix the issue timely
          const { component: Component, ...otherProps } = route;
          return (
            <Route key={route.path} {...otherProps}>
              <BoundsRedirect route={route}>
                <Renderer component={<Component fallback={<LoadingStatus />} />} />
              </BoundsRedirect>
            </Route>
          );
        })}
        <Route>
          <Renderer component={<NotFound />} />
        </Route>
      </Switch>
    </Fragment>
  );
};

export default Layout;
